import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { companyInfo as defaultCompanyInfo, contactPageContent as defaultContactContent } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [content, setContent] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/content`);
        setContent(response.data);
      } catch (error) {
        console.error('Error fetching content:', error);
      }
    };
    fetchContent();
  }, []);

  // Use database content or fallback to mock data
  const companyInfo = {
    phone: content?.phone || defaultCompanyInfo.phone,
    email: content?.email || defaultCompanyInfo.email,
    address: content?.address || defaultCompanyInfo.address,
    workingHours: content?.working_hours || defaultCompanyInfo.workingHours
  };

  const contactPageContent = {
    title: content?.contact_title || defaultContactContent.title,
    subtitle: content?.contact_subtitle || defaultContactContent.subtitle,
    demoText: content?.demo_text || defaultContactContent.demoText
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      
      // Reset form after delay
      setTimeout(() => {
        setFormData({ name: '', company: '', phone: '', email: '', message: '' });
        setIsSubmitted(false);
      }, 3000);
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactDetails = [
    {
      icon: Phone,
      label: 'Phone',
      value: companyInfo.phone,
      href: `tel:${companyInfo.phone}`
    },
    {
      icon: Mail,
      label: 'Email',
      value: companyInfo.email,
      href: `mailto:${companyInfo.email}`
    },
    {
      icon: MapPin,
      label: 'Address',
      value: companyInfo.address,
      href: null
    },
    {
      icon: Clock,
      label: 'Working Hours',
      value: companyInfo.workingHours,
      href: null
    }
  ];

  return (
    <div className="min-h-screen bg-gray-light">
      {/* Hero Section */}
      <section className="bg-navy pt-44 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            {contactPageContent.title}
          </h1>
          <p className="text-xl text-white/70 max-w-2xl mx-auto">
            {contactPageContent.subtitle}
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Info */}
            <div>
              {/* Demo Scheduling Card */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-red-600/10 rounded-xl flex items-center justify-center">
                    <Calendar className="h-6 w-6 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-navy">Schedule a Demo</h3>
                </div>
                <p className="text-navy/70 leading-relaxed">
                  {contactPageContent.demoText}
                </p>
              </div>

              {/* Contact Details */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h3 className="text-xl font-bold text-navy mb-6">Contact Information</h3>
                <div className="space-y-6">
                  {contactDetails.map((detail, index) => {
                    const Icon = detail.icon;
                    const content = (
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-navy/5 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon className="h-5 w-5 text-red-600" />
                        </div>
                        <div>
                          <p className="text-sm text-navy/50 font-medium">{detail.label}</p>
                          <p className="text-navy font-semibold">{detail.value}</p>
                        </div>
                      </div>
                    );

                    return detail.href ? (
                      <a 
                        key={index} 
                        href={detail.href}
                        className="block hover:bg-gray-50 -mx-4 px-4 py-2 rounded-lg transition-colors"
                      >
                        {content}
                      </a>
                    ) : (
                      <div key={index}>{content}</div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <h3 className="text-xl font-bold text-navy mb-6">Send Us a Message</h3>
              
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                  <h4 className="text-xl font-semibold text-navy mb-2">Message Sent!</h4>
                  <p className="text-navy/60">We'll get back to you as soon as possible.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <Label htmlFor="name" className="text-navy font-medium">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      className="mt-1.5 bg-gray-50 border-gray-200 focus:border-red-500 focus:ring-red-500"
                    />
                  </div>

                  <div>
                    <Label htmlFor="company" className="text-navy font-medium">Company</Label>
                    <Input
                      id="company"
                      name="company"
                      type="text"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your company name"
                      className="mt-1.5 bg-gray-50 border-gray-200 focus:border-red-500 focus:ring-red-500"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="phone" className="text-navy font-medium">Phone *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(123) 456-7890"
                        className="mt-1.5 bg-gray-50 border-gray-200 focus:border-red-500 focus:ring-red-500"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-navy font-medium">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="you@example.com"
                        className="mt-1.5 bg-gray-50 border-gray-200 focus:border-red-500 focus:ring-red-500"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-navy font-medium">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your needs..."
                      className="mt-1.5 bg-gray-50 border-gray-200 focus:border-red-500 focus:ring-red-500 resize-none"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-6 text-lg transition-all duration-300"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        Send Message
                        <Send className="ml-2 h-5 w-5" />
                      </span>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
